import { InteractionWorkspaceProps } from "./types";
import { useStyle } from "./style";
import { MessagingChatInteractionContent } from "@/components/molecules/interactionWorkspace";
import { Flex, GapSize } from "@/components/atoms/flex";
import { Button, Divider, Spinner } from "@fluentui/react-components";
import { useQuery } from "@tanstack/react-query";
import { VoiceEndpointsKey } from "@/repository/queryFns";
import { useIntl } from "react-intl";
import ReactPlayerWrapper from "@/components/atoms/reactPlayer/ReactPlayerWrapper";
import { ArrowDownload20Regular } from "@fluentui/react-icons";
import { auditRecordingDownload, auditRecordingPlay, getCallRecording, RecordingChunkUrl } from "@/repository/queryFns/history";

export const VoiceHistoryInteractionContent = (
	props: InteractionWorkspaceProps
) => {
	const { historyItem } = props;
	const classes = useStyle();
	const intl = useIntl();
	const {
		data: recordings,
		isLoading,
		isError,
	} = useQuery<RecordingChunkUrl[]>({
		queryKey: [VoiceEndpointsKey, historyItem.interactionId],
		queryFn: () => getCallRecording(historyItem.interactionId),
		enabled: historyItem.isUnify,
	});

	const handlePlay = (recordingId: string) => {
		auditRecordingPlay(recordingId).catch((err) => {
			// eslint-disable-next-line no-console
			console.debug("auditRecordingPlay failed (non-blocking):", err);
		});
	};

	const handleDownload = (recording: RecordingChunkUrl) => {
		auditRecordingDownload(recording.recordingId).catch((err) => {
			// eslint-disable-next-line no-console
			console.debug("auditRecordingDownload failed (non-blocking):", err);
		});

		// Open in a new context (matches Admin Center's download): the recording URL is a
		// cross-origin blob SAS with no attachment disposition, so an anchor would ignore
		// `download` and navigate the current Teams tab, replacing the app view.
		window.open(recording.url, "_blank", "noopener");
	};

	const recordingsPlayer =
		!recordings || recordings.length === 0 ? (
			<span>
				{intl.formatMessage({
					id: "Interaction.Recordings.FileNotAvailable",
				})}
			</span>
		) : (
			<Flex
				vAlign="flex-start"
				gap={GapSize.Large}
				className={classes.recordingsScroll}
			>
				{recordings.map((r) => (
					<Flex key={r.recordingId} vAlign="center">
						<ReactPlayerWrapper
							url={r.url}
							onPlay={() => handlePlay(r.recordingId)}
							hideNativeDownload
						/>
						<Button
							appearance="subtle"
							icon={<ArrowDownload20Regular />}
							title={intl.formatMessage({ id: "Interaction.Recordings.Download" })}
							aria-label={intl.formatMessage({ id: "Interaction.Recordings.Download" })}
							onClick={() => handleDownload(r)}
						/>
					</Flex>
				))}
			</Flex>
		);
	const renderRecordings = isError ? (
		<span>
			{intl.formatMessage({
				id: "Interaction.Recordings.FailedToLoad",
			})}
		</span>
	) : (
		recordingsPlayer
	);

	return (
		<Flex column className={classes.parent}>
			{historyItem.isUnify && (
				<>
					<Flex hAlign="center" vAlign="flex-start" className={classes.audioPlayer}>
						{isLoading ? (
							<Spinner
								size="tiny"
								label={intl.formatMessage({ id: "Common.Loading" })}
							/>
						) : (
							renderRecordings
						)}
					</Flex>

					<Divider className={classes.middle} />
				</>
			)}

			<div className={classes.bottom}>
				<MessagingChatInteractionContent
					hideSendBox
					isHistory
					interactionId={historyItem.interactionId}
					type={historyItem.type}
					date={historyItem.arrivalTime.toString()}
				/>
			</div>
		</Flex>
	);
};
